/**
 * Ticket status presentation — ERPat's OWN wording and colour for each raw status.
 *
 * The server sends `status_label` (`lang($status)` on the web) with every ticket,
 * so the label normally comes from there. This table is the fallback for an older
 * backend, and the colour map mirrors the web ticket screen:
 *
 *   new            → warning (orange)   "New"
 *   open           → danger  (red)      "Open"
 *   client_replied → danger  (red)      "Client replied"
 *   on_hold        → muted   (grey)     "On Hold"
 *   closed         → success (green)    "Closed"
 *
 * Lives in utils/ (not views/) because services/widgets.ts needs it too.
 */
import type { TicketRawStatus } from '@/types/ticket';

export const TICKET_RAW_STATUSES: readonly TicketRawStatus[] = ['new', 'open', 'client_replied', 'on_hold', 'closed'];

/** The web's labels (application/language/english/default_lang.php). */
export const TICKET_STATUS_LABELS: Record<TicketRawStatus, string> = {
  new: 'New',
  open: 'Open',
  client_replied: 'Client replied',
  on_hold: 'On Hold',
  closed: 'Closed',
};

export type TicketStatusTone = 'warning' | 'danger' | 'muted' | 'success';

const TONES: Record<TicketRawStatus, TicketStatusTone> = {
  new: 'warning',
  open: 'danger',
  client_replied: 'danger',
  on_hold: 'muted',
  closed: 'success',
};

export const ticketStatusTone = (status: TicketRawStatus): TicketStatusTone => TONES[status];

/** The server's label when it sent one, the web's wording otherwise. */
export function ticketStatusLabel(status: TicketRawStatus, serverLabel?: string): string {
  const label = (serverLabel ?? '').trim();
  return label !== '' ? label : TICKET_STATUS_LABELS[status];
}

/**
 * The status filters the ticket list and dashboard offer — ERPat's browse filter
 * (New, Open, On Hold, Closed). "Open" covers `client_replied` too, as on the web,
 * where the browse filter has no separate "Client replied" option.
 */
export type TicketStatusFilter = 'new' | 'open' | 'on_hold' | 'closed';

export const TICKET_STATUS_FILTERS: readonly TicketStatusFilter[] = ['new', 'open', 'on_hold', 'closed'];

export const TICKET_FILTER_LABELS: Record<TicketStatusFilter, string> = {
  new: 'New',
  open: 'Open',
  on_hold: 'On Hold',
  closed: 'Closed',
};

export function ticketStatusFilterOf(status: TicketRawStatus): TicketStatusFilter {
  return status === 'client_replied' ? 'open' : status;
}
